
import { useNavigate } from 'react-router-dom';
import { Button } from "@/components/ui/button";
import { ArrowLeft, Chrome, Bot, CheckCircle, XCircle, BarChart3, Zap, Brain, Code } from "lucide-react";
import { Head } from 'vite-react-ssg';
import { Card, CardContent } from "@/components/ui/card";
import NavigationButtons from '@/components/NavigationButtons';
import { WaitlistDialog } from '@/components/WaitlistDialog';
import RelatedLinks from '@/components/RelatedLinks';
import { useState } from 'react';

const Llama4VsDeepseek = () => {
  const navigate = useNavigate();
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  return (
    <>
      <Head>
        <title>Llama 4 vs DeepSeek AI — Complete Model Comparison</title>
        <meta name="description" content="Compare Llama 4 and DeepSeek AI models across performance benchmarks, capabilities, and use cases. Discover which AI model is best for your specific needs." />
        <meta name="keywords" content="Llama 4 vs DeepSeek, AI model comparison, DeepSeek vs Llama, language model benchmarks, AI capabilities, open-source AI models" />
        <link rel="canonical" href="https://deepseek.ai/blog/llama-4-vs-deepseek" />
        
        <meta property="og:title" content="Llama 4 vs DeepSeek AI: Complete Model Comparison" />
        <meta property="og:description" content="Compare Llama 4 and DeepSeek AI models across performance benchmarks, capabilities, and use cases." />
        <meta property="og:type" content="article" />
        <meta property="og:url" content="https://deepseek.ai/blog/llama-4-vs-deepseek" />
        <meta property="og:image" content="https://deepseek.ai/og-image.png" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta name="twitter:image" content="https://deepseek.ai/og-image.png" />
        
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Llama 4 vs DeepSeek AI: Complete Model Comparison" />
        <meta name="twitter:description" content="Compare Llama 4 and DeepSeek AI models across performance benchmarks, capabilities, and use cases." />
      </Head>

      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pt-24 pb-16">
          <Button
            variant="ghost"
            onClick={() => navigate('/blog')}
            className="mb-8 hover:bg-gray-100"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Blog
          </Button>

          <NavigationButtons />
          
          <article className="mt-8">
            <div className="mb-8">
              <div className="text-gray-500 mb-2">
                April 10, 2025 • DeepSeek Research Team
              </div>
              <h1 className="text-4xl font-bold text-gray-900 mb-4">Llama 4 vs DeepSeek AI: Comprehensive Comparison of Leading AI Models</h1>
              <p className="text-lg text-gray-700">
                As open-source AI models continue to advance, two major players have emerged as frontrunners: Meta's Llama 4 and DeepSeek AI. This article provides a detailed comparison to help you understand the strengths, weaknesses, and ideal use cases for each model.
              </p>
            </div>

            <Card className="mb-10 bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-100">
              <CardContent className="pt-6">
                <div className="flex items-center mb-4">
                  <Brain className="h-6 w-6 text-blue-600 mr-2" />
                  <h2 className="text-2xl font-bold text-gray-900">Key Takeaways</h2>
                </div>
                <ul className="space-y-2">
                  <li className="flex items-start">
                    <CheckCircle className="h-5 w-5 text-green-500 mr-2 shrink-0 mt-0.5" />
                    <span>DeepSeek excels in multilingual support and specialized knowledge domains</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="h-5 w-5 text-green-500 mr-2 shrink-0 mt-0.5" />
                    <span>Llama 4 offers greater performance on standard English-language benchmarks</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="h-5 w-5 text-green-500 mr-2 shrink-0 mt-0.5" />
                    <span>Both models represent significant advances in open-source AI capabilities</span>
                  </li>
                </ul>
              </CardContent>
            </Card>

            <div className="prose prose-lg max-w-none mb-10">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Model Overview</h2>
              
              <div className="grid md:grid-cols-2 gap-6 mb-8">
                <Card className="border-blue-200">
                  <CardContent className="pt-6">
                    <h3 className="text-xl font-bold text-blue-700 mb-3">DeepSeek AI</h3>
                    <p className="mb-4">DeepSeek AI is a cutting-edge AI model developed in China that has rapidly gained attention for its strong performance across various domains, particularly in specialized knowledge and multilingual capabilities.</p>
                    <ul className="space-y-1.5">
                      <li className="flex items-center text-sm">
                        <CheckCircle className="h-4 w-4 text-green-500 mr-2 shrink-0" />
                        <span>Latest version: DeepSeek V3.1</span>
                      </li>
                      <li className="flex items-center text-sm">
                        <CheckCircle className="h-4 w-4 text-green-500 mr-2 shrink-0" />
                        <span>Open-source foundation</span>
                      </li>
                      <li className="flex items-center text-sm">
                        <CheckCircle className="h-4 w-4 text-green-500 mr-2 shrink-0" />
                        <span>Specialized variants for coding & research</span>
                      </li>
                    </ul>
                  </CardContent>
                </Card>

                <Card className="border-purple-200">
                  <CardContent className="pt-6">
                    <h3 className="text-xl font-bold text-purple-700 mb-3">Llama 4</h3>
                    <p className="mb-4">Llama 4 is Meta's latest large language model, building on its predecessors with improved reasoning, safety, and knowledge capabilities. It's designed for both research and commercial applications.</p>
                    <ul className="space-y-1.5">
                      <li className="flex items-center text-sm">
                        <CheckCircle className="h-4 w-4 text-green-500 mr-2 shrink-0" />
                        <span>Latest version: Llama 4</span>
                      </li>
                      <li className="flex items-center text-sm">
                        <CheckCircle className="h-4 w-4 text-green-500 mr-2 shrink-0" />
                        <span>Developed by Meta's AI team</span>
                      </li>
                      <li className="flex items-center text-sm">
                        <CheckCircle className="h-4 w-4 text-green-500 mr-2 shrink-0" />
                        <span>Multiple model sizes (8B, 70B, etc.)</span>
                      </li>
                    </ul>
                  </CardContent>
                </Card>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4">Performance Comparison</h2>
              
              <p className="mb-4">When comparing DeepSeek AI and Llama 4, several performance dimensions stand out across benchmarks and real-world usage:</p>
              
              <h3 className="text-xl font-bold text-gray-800 flex items-center mb-3">
                <BarChart3 className="h-5 w-5 mr-2 text-blue-600" />
                Benchmark Performance
              </h3>
              
              <div className="overflow-x-auto mb-6">
                <table className="min-w-full border border-gray-200 rounded-lg">
                  <thead>
                    <tr className="bg-gray-50">
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-600 border-b">Benchmark</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-blue-600 border-b">DeepSeek</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-purple-600 border-b">Llama 4</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-600 border-b">Advantage</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 bg-white">
                    <tr>
                      <td className="px-4 py-3 text-sm text-gray-800">MMLU (General Knowledge)</td>
                      <td className="px-4 py-3 text-sm">78.2%</td>
                      <td className="px-4 py-3 text-sm">82.5%</td>
                      <td className="px-4 py-3 text-sm text-purple-600">Llama 4</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-sm text-gray-800">GSM8K (Math Reasoning)</td>
                      <td className="px-4 py-3 text-sm">80.8%</td>
                      <td className="px-4 py-3 text-sm">78.3%</td>
                      <td className="px-4 py-3 text-sm text-blue-600">DeepSeek</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-sm text-gray-800">HumanEval (Coding)</td>
                      <td className="px-4 py-3 text-sm">74.6%</td>
                      <td className="px-4 py-3 text-sm">67.2%</td>
                      <td className="px-4 py-3 text-sm text-blue-600">DeepSeek</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-sm text-gray-800">HELM (Overall)</td>
                      <td className="px-4 py-3 text-sm">71.4%</td>
                      <td className="px-4 py-3 text-sm">73.8%</td>
                      <td className="px-4 py-3 text-sm text-purple-600">Llama 4</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              
              <h3 className="text-xl font-bold text-gray-800 flex items-center mb-3">
                <Zap className="h-5 w-5 mr-2 text-amber-500" />
                Specialized Capabilities
              </h3>
              
              <div className="grid md:grid-cols-2 gap-6 mb-8">
                <div>
                  <h4 className="font-bold text-blue-700 mb-2">DeepSeek Strengths</h4>
                  <ul className="space-y-2">
                    <li className="flex items-start">
                      <CheckCircle className="h-4 w-4 text-green-500 mr-2 shrink-0 mt-1" />
                      <span>Exceptional performance on mathematical and scientific tasks</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="h-4 w-4 text-green-500 mr-2 shrink-0 mt-1" />
                      <span>Superior multilingual capabilities, especially for Asian languages</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="h-4 w-4 text-green-500 mr-2 shrink-0 mt-1" />
                      <span>Extended context window (128K tokens)</span>
                    </li>
                  </ul>
                </div>
                
                <div>
                  <h4 className="font-bold text-purple-700 mb-2">Llama 4 Strengths</h4>
                  <ul className="space-y-2">
                    <li className="flex items-start">
                      <CheckCircle className="h-4 w-4 text-green-500 mr-2 shrink-0 mt-1" />
                      <span>Strong performance on general knowledge benchmarks</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="h-4 w-4 text-green-500 mr-2 shrink-0 mt-1" />
                      <span>Better factual accuracy and reduced hallucinations</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="h-4 w-4 text-green-500 mr-2 shrink-0 mt-1" />
                      <span>Robust safety measures and content moderation</span>
                    </li>
                  </ul>
                </div>
              </div>
              
              <h3 className="text-xl font-bold text-gray-800 flex items-center mb-3">
                <Code className="h-5 w-5 mr-2 text-blue-600" />
                Programming & Technical Tasks
              </h3>
              
              <p className="mb-4">Both models excel at code-related tasks, but with differing strengths:</p>
              
              <div className="grid md:grid-cols-2 gap-6 mb-8">
                <Card className="border-blue-100 bg-blue-50/50">
                  <CardContent className="pt-6">
                    <h4 className="font-bold text-blue-700 mb-2">DeepSeek Coder</h4>
                    <p className="mb-3">DeepSeek offers a specialized coding variant that demonstrates exceptional performance across multiple programming languages.</p>
                    <ul className="space-y-1.5 text-sm">
                      <li className="flex items-center">
                        <CheckCircle className="h-4 w-4 text-green-500 mr-2 shrink-0" />
                        <span>Top-tier performance on HumanEval and MBPP benchmarks</span>
                      </li>
                      <li className="flex items-center">
                        <CheckCircle className="h-4 w-4 text-green-500 mr-2 shrink-0" />
                        <span>Excels at complex algorithm implementation</span>
                      </li>
                      <li className="flex items-center">
                        <CheckCircle className="h-4 w-4 text-green-500 mr-2 shrink-0" />
                        <span>Strong understanding of Chinese-language codebase documentation</span>
                      </li>
                    </ul>
                  </CardContent>
                </Card>

                <Card className="border-purple-100 bg-purple-50/50">
                  <CardContent className="pt-6">
                    <h4 className="font-bold text-purple-700 mb-2">Llama 4 Coding</h4>
                    <p className="mb-3">While not offering a specialized coding variant, Llama 4 demonstrates strong general coding capabilities.</p>
                    <ul className="space-y-1.5 text-sm">
                      <li className="flex items-center">
                        <CheckCircle className="h-4 w-4 text-green-500 mr-2 shrink-0" />
                        <span>Good performance across popular programming languages</span>
                      </li>
                      <li className="flex items-center">
                        <CheckCircle className="h-4 w-4 text-green-500 mr-2 shrink-0" />
                        <span>Excels at explaining code and debugging</span>
                      </li>
                      <li className="flex items-center">
                        <XCircle className="h-4 w-4 text-red-500 mr-2 shrink-0" />
                        <span>Less specialized than dedicated coding models</span>
                      </li>
                    </ul>
                  </CardContent>
                </Card>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4">Which Model Should You Choose?</h2>
              
              <p className="mb-6">Your choice between DeepSeek AI and Llama 4 should depend on your specific use case:</p>
              
              <Card className="mb-6 border-gray-200">
                <CardContent className="pt-6">
                  <h3 className="text-xl font-bold text-gray-800 mb-3">Choose DeepSeek AI if:</h3>
                  <ul className="space-y-2">
                    <li className="flex items-start">
                      <CheckCircle className="h-5 w-5 text-green-500 mr-2 shrink-0 mt-0.5" />
                      <span>You need strong multilingual support, especially for Asian languages</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="h-5 w-5 text-green-500 mr-2 shrink-0 mt-0.5" />
                      <span>Your applications involve complex mathematical or scientific reasoning</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="h-5 w-5 text-green-500 mr-2 shrink-0 mt-0.5" />
                      <span>You need specialized coding capabilities</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="h-5 w-5 text-green-500 mr-2 shrink-0 mt-0.5" />
                      <span>Your use cases require processing very long contexts</span>
                    </li>
                  </ul>
                </CardContent>
              </Card>
              
              <Card className="mb-8 border-gray-200">
                <CardContent className="pt-6">
                  <h3 className="text-xl font-bold text-gray-800 mb-3">Choose Llama 4 if:</h3>
                  <ul className="space-y-2">
                    <li className="flex items-start">
                      <CheckCircle className="h-5 w-5 text-green-500 mr-2 shrink-0 mt-0.5" />
                      <span>General knowledge and factual accuracy are top priorities</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="h-5 w-5 text-green-500 mr-2 shrink-0 mt-0.5" />
                      <span>You need robust safety features and content moderation</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="h-5 w-5 text-green-500 mr-2 shrink-0 mt-0.5" />
                      <span>You're developing primarily for English-language applications</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="h-5 w-5 text-green-500 mr-2 shrink-0 mt-0.5" />
                      <span>You want to leverage Meta's extensive ecosystem and support</span>
                    </li>
                  </ul>
                </CardContent>
              </Card>
              
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Conclusion</h2>
              
              <p className="mb-4">
                Both DeepSeek AI and Llama 4 represent significant advancements in open-source AI capabilities, with each offering distinctive strengths. DeepSeek excels in specialized domains and multilingual support, while Llama 4 offers strong general performance and safety features.
              </p>
              
              <p className="mb-4">
                For many organizations, the optimal approach might involve leveraging both models in different contexts, or selecting the one that best aligns with their specific use cases and requirements. As these models continue to evolve rapidly, we can expect to see further improvements in capabilities and performance.
              </p>
              
            </div>

            <div className="mt-12 mb-12 border-t border-b border-gray-200 py-8">
              <h2 className="text-2xl font-bold mb-6">Related Articles</h2>
              <RelatedLinks />
            </div>

            <div className="flex flex-col sm:flex-row justify-center gap-4 mt-12">
              <a
                href="https://chromewebstore.google.com/detail/ai-sidebar-with-deepseek/inhcgfpbfdjbjogdfjbclgolkmhnooop"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center px-8 py-4 text-base font-medium rounded-full text-white bg-[#0066FF] hover:bg-[#0066FF]/90 transition-all shadow-[0_4px_14px_0_rgba(0,102,255,0.39)] hover:shadow-[0_6px_20px_rgba(0,102,255,0.23)] hover:transform hover:translate-y-[-1px]"
                aria-label="Add to Chrome - It's Free"
              >
                <Chrome className="mr-2 h-5 w-5" aria-hidden="true" />
                Add to Chrome - It's Free
              </a>
              <button
                onClick={() => setIsDialogOpen(true)}
                className="inline-flex items-center justify-center px-6 py-3 border-2 border-[#0066FF] text-base font-medium rounded-full text-[#0066FF] bg-white hover:bg-blue-50 transition-all shadow-[0_4px_14px_0_rgba(0,102,255,0.39)] hover:shadow-[0_6px_20px_rgba(0,102,255,0.23)] hover:transform hover:translate-y-[-1px]"
                aria-label="Create AI Agents"
              >
                Create AI Agents
                <Bot className="ml-2 h-5 w-5" aria-hidden="true" />
              </button>
            </div>
          </article>
        </div>
      </div>

      <WaitlistDialog 
        isOpen={isDialogOpen} 
        onClose={() => setIsDialogOpen(false)} 
      />
    </>
  );
};

export default Llama4VsDeepseek;
